export {
  test,
  validate,
  type ValidationMapperFunc,
  type FormErrorShape,
  type TestResultShape,
} from "./helpers/validation";
import { filterEntriesRequiringOther } from "@porchpass/ui";
import {
  DLA_ETHNICITY_TYPES_REQUIRING_OTHER,
  DLA_RACE_TYPES_REQUIRING_OTHER,
} from "src/forms/validation/conditionalOtherConfigs";
import type { BiologicalSexType, Ethnicity, Race } from "src/types/ethnicity";

type TestFunction = (val: string) => boolean;
type AllowEmptyStringFunction = (func: TestFunction) => TestFunction;

const allowEmptyString: AllowEmptyStringFunction = (func) => (val) =>
  Boolean(!val || val?.length === 0 || func(val));

export const isSet = (val: unknown): boolean => typeof val !== "undefined";

export const notEmpty = (val: string): boolean =>
  Boolean(val && val.length > 0);

export const validName = allowEmptyString((val: string): boolean =>
  Boolean(val && /^[A-Za-zÀ-ÖØ-öø-ÿ '-]+$/.test(val)),
);

export const validEmail = allowEmptyString((val: string): boolean =>
  Boolean(
    val &&
      /^[a-zA-Z0-9._%+-]+@(?!(?:[-.])|.*\.\.)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/.test(
        val,
      ),
  ),
);

export const isValidPhoneNumber = (value: string): boolean =>
  /^\d{10}$/.test(value);

export const isValidPhoneNumberWithCountryCode = (value: string): boolean =>
  /^\+1\d{10}$|^\d{10}$/.test(value);

export const validStreetAddress = allowEmptyString((val: string): boolean =>
  Boolean(val && /\w+\s\w+/.test(val)),
);

export const validZipcode = allowEmptyString((val: string): boolean =>
  Boolean(val && /^\d{5}(?:[-\s]\d{4})?$/.test(val)),
);

export const commaSeparatedDigits = (val: string): boolean =>
  Boolean(val && /^(\d+)(, ?\d+)*$/.test(val));

export const lessThanMaxLength =
  (maxLength: number): TestFunction =>
  (val: string) =>
    Boolean(!val || val.length <= maxLength);

export const greaterThanMinAmount =
  (minAmount: number, isIncludeSelf?: boolean): TestFunction =>
  (val: string) =>
    Boolean(
      !val ||
        (isIncludeSelf ? Number(val) >= minAmount : Number(val) > minAmount),
    );

export const lessThanMaxAmount =
  (maxAmount: number, isIncludeSelf?: boolean): TestFunction =>
  (val: string) =>
    Boolean(
      !val ||
        (isIncludeSelf ? Number(val) <= maxAmount : Number(val) < maxAmount),
    );

export const validDateRange = (
  fromMonth: string,
  fromYear: string,
  toMonth: string,
  toYear: string,
): boolean => {
  if (!toMonth || !toYear) return true;
  if (toYear < fromYear) return false;
  if (toYear === fromYear && toMonth < fromMonth) return false;
  return true;
};

export const validAges = (val: string): boolean => {
  if (!val) return true;
  const ages = val.split(",").map((age) => age.trim());
  for (const age of ages) {
    const num = Number(age);
    if (isNaN(num) || num < 0 || num > 120) {
      return false;
    }
  }
  return true;
};

export const isPastOrCurrentDate = (
  month: string,
  year: string,
  day?: string,
): boolean => {
  const currentDate = new Date();
  const inputDate = new Date(
    Number(year),
    Number(month) - 1,
    day ? Number(day) : 1,
  );
  return inputDate < currentDate;
};

export const isPastDateBeyondLimit = (
  selectedMonth: number,
  selectedYear: number,
  currentMonth: number,
  earliestAllowedYear: number,
) => {
  return Boolean(
    selectedYear < earliestAllowedYear ||
      (selectedYear === earliestAllowedYear && selectedMonth < currentMonth),
  );
};

export const validSSN = (ssnPart: "start" | "middle" | "end") => {
  const validLength = {
    start: 3,
    middle: 2,
    end: 4,
  };
  return (val: string) => Boolean(val?.length === validLength[ssnPart]);
};

export const isValidDate = (
  month: number,
  day: number,
  year: number,
): boolean => {
  if (!year || !month || !day) return true;
  const date = new Date(year, month - 1, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

export const hasBiologicalSex = (bioSex: BiologicalSexType[]): boolean =>
  bioSex && bioSex.length > 0;

export const hasEthnicity = (ethnicities: Ethnicity[]): boolean =>
  ethnicities && ethnicities.length > 0;

export const isEthnicityOtherSectionValid = (
  ethnicities: Ethnicity[],
): boolean => {
  if (!ethnicities) return false;
  const entries = ethnicities.map((e) => ({
    type: e.ethnicity_type ?? "",
    other: e.ethnicity_other,
  }));
  return (
    filterEntriesRequiringOther(entries, DLA_ETHNICITY_TYPES_REQUIRING_OTHER)
      .length === entries.length
  );
};

export const hasRace = (races: Race[]): boolean => races && races.length > 0;

export const isRaceOtherSectionValid = (races: Race[]): boolean => {
  if (!races) return false;
  const entries = races.map((r) => ({
    type: r.race_type ?? "",
    other: r.race_other,
  }));
  return (
    filterEntriesRequiringOther(entries, DLA_RACE_TYPES_REQUIRING_OTHER)
      .length === entries.length
  );
};

export const isAgeValid = (
  month: number,
  day: number,
  year: number,
  minAge: number = 18,
): boolean => {
  if (!isValidDate(month, day, year)) return false;
  const birthDate = new Date(year, month - 1, day);

  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();

  const hasHadBirthday =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  return hasHadBirthday ? age >= minAge : age - 1 >= minAge;
};
